import { Col, Row, Space } from 'antd';
import React, { FC, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  SlackConfig,
  SlackProjectSettings,
} from 'api/integrations/slack/slack.types';
import { Button } from 'components/atoms/button';
import { PageLoading } from 'components/atoms/loading';
import { Radio } from 'components/atoms/radio';
import { Page } from 'components/molecules/page';
import { useSlackActions } from 'components/pages/integrations/slack/use-slack-actions';
import { Caption01, ProductiveHeading07 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { SelectChannelModal } from './select-channel-modal';
import { useProjectSlackNotification } from './use-project-slack-notification';

const PageContent = styled(Page.Content)`
  padding: var(--layout-03);

  > .ant-row {
    padding: var(--spacing-03);
  }
`;

const Title = styled.div`
  ${ProductiveHeading07};
  margin-bottom: var(--spacing-05);
`;

const ChannelTitle = styled.div`
  ${ProductiveHeading07};
  margin: var(--spacing-03) 0 0 var(--spacing-06);
`;

const StyledButton = styled(Button)`
  padding-left: var(--spacing-06);
`;

const Caption = styled.div`
  ${Caption01};
  padding-left: var(--spacing-06);
`;

interface SlackNotificationProps {
  projectId: number;
  config: SlackConfig;
}

export const SlackNotifications: FC<SlackNotificationProps> = ({
  projectId,
  config,
}: SlackNotificationProps) => {
  const { channelsList, loadingChannelsList } = useSlackActions(config);
  const {
    projectNotifySettings,
    handleSettingsChange,
    isModalVisible,
    setShowModal,
    status,
  } = useProjectSlackNotification(projectId);
  const title = useTranslation('project.settings.slack.notification');
  const buttonText = useTranslation('project.settings.slack.change.channel');
  const hideDrawer = useCallback(() => {
    setShowModal(false);
  }, [setShowModal]);

  const handleChannelChange = useCallback(
    (channel) => {
      const selectedChannel = channelsList?.find((_channel) => {
        return channel.value === _channel.id;
      });

      if (selectedChannel && projectNotifySettings) {
        handleSettingsChange({
          ...projectNotifySettings,
          channel: selectedChannel as SlackProjectSettings['channel'],
        });
      }
      hideDrawer();
    },
    [channelsList, handleSettingsChange, hideDrawer, projectNotifySettings],
  );

  return (
    <Page>
      <Helmet>{title}</Helmet>
      <Page.Header
        $white
        title={<Page.Header.Title>{title}</Page.Header.Title>}
        sticky
      />
      {status === 'loading' ? (
        <PageLoading />
      ) : (
        <PageContent>
          {projectNotifySettings && status === 'success' ? (
            <Row gutter={32} align="top">
              <Col span={32}>
                <Title>
                  <FormattedMessage id="project.settings.slack.notification.heading" />
                </Title>
                <Radio.Group
                  onChange={(e) => {
                    handleSettingsChange({
                      ...projectNotifySettings,
                      doNoNotify: e.target.value,
                    });
                  }}
                  value={projectNotifySettings?.doNoNotify}
                >
                  <Space direction="vertical">
                    <Radio value={false}>
                      <FormattedMessage id="project.settings.slack.select.channel" />

                      <ChannelTitle>
                        {projectNotifySettings?.channel?.name
                          ? `#${projectNotifySettings.channel.name}`
                          : ''}
                      </ChannelTitle>

                      <StyledButton
                        onClick={() => setShowModal(true)}
                        type="link"
                      >
                        {buttonText}
                      </StyledButton>
                    </Radio>
                    <Radio value>
                      <FormattedMessage id="project.settings.slack.do_not_notify" />
                      <Caption>
                        <FormattedMessage id="project.settings.slack.do_not_notify.caption" />
                      </Caption>
                    </Radio>
                  </Space>
                </Radio.Group>
                {isModalVisible && (
                  <SelectChannelModal
                    value={{
                      label: projectNotifySettings?.channel?.name,
                      value: projectNotifySettings?.channel?.id,
                    }}
                    channelsList={channelsList || []}
                    onClose={hideDrawer}
                    onSelect={handleChannelChange}
                    loading={loadingChannelsList}
                  />
                )}
              </Col>
            </Row>
          ) : null}
        </PageContent>
      )}
    </Page>
  );
};
